// src/services/ticketDesignApi.js
import environment from "../environments/environment";
import { useSite } from "../store/useSite";

const LS_PREFIX = "qe_ticket_design:";

// Only for preview
export function sampleTicketData() {
  let defaultEventName = "Your Event Name";
  try {
    const eventData = useSite.getState().eventData;
    if (eventData?.event_name) {
      defaultEventName = eventData.event_name;
    }
  } catch (e) {}

  let defaultTicketName = "VIP / Standard Admission";
  try {
    const rawTicketName = localStorage.getItem("ticketClassName");
    if (rawTicketName) {
      defaultTicketName = rawTicketName;
    }
  } catch(e) {}

  return {
    eventName: defaultEventName,
    date: "09 Jan 2026 to 20 Jan 2026 | 08:00 AM",
    ticketValidLabel: "Ticket Valid",
    ticketValidDates: "09 Jan 2026 to 20 Jan 2026",
    name: "Lakshmi Priyadarshini Venkata Subbalakshmi",
    email: "Piyushlakhesar@Quickevents.Co.In",
    qrData: environment.defaultQrPreviewUrl,
    profile: environment.defaultTicketImageUrl,
    ticketId: "JSKT9XAB1H",
    ticketName: defaultTicketName,
    ticketClassName: "",
    bookedBy: "Lakshmi Priyadarshini Venkata Subbalakshmi on 05-09-2026",
    company_name: "Acme Innovations Ltd.",
    companyName: "Acme Innovations Ltd.",
    company: "Acme Innovations Ltd.",
    "Company Name": "Acme Innovations Ltd.",
    organization: "Acme Innovations Ltd.",
    Organization: "Acme Innovations Ltd.",
    location: "Grand Convention Center",
    venue: "Indira Gandhi National Auditorium",
  };
}

// --- API helpers ---

export async function getTicketDesign(eventId, ticketClassId, type = 'ticket_class', tag = 'standard') {
  if (!eventId || !ticketClassId) {
    console.warn("getTicketDesign: missing eventId or ticketClassId");
    return null;
  }

  const query = new URLSearchParams({ type, tag }).toString();
  const response = await fetch(
    `${environment.apiUrl}/dynamic-ticket/ticketDesign/${eventId}/${ticketClassId}?${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  const result = await response.json();
  try {
    localStorage.setItem(LS_PREFIX + `${eventId}:${ticketClassId}:${tag}`, JSON.stringify(result?.data?.design));
  } catch (e) {
    console.warn("localStorage setItem failed (quota exceeded):", e);
  }
  // The backend might return the design inside result.data.design or result.design depending on responseBuilder usage
  return result?.data?.design || result?.design || null;
}

export async function getTicketDesignDetails(eventId, ticketClassId, type = 'ticket_class', tag = 'standard') {
  if (!eventId || !ticketClassId) {
    console.warn("getTicketDesignDetails: missing eventId or ticketClassId");
    return null;
  }

  const query = new URLSearchParams({ type, tag }).toString();
  const response = await fetch(
    `${environment.apiUrl}/dynamic-ticket/ticketDesign/${eventId}/${ticketClassId}?${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  const result = await response.json();
  const data = result?.data || result || {};
  const design = data?.design || null;
  try {
    localStorage.setItem(LS_PREFIX + `${eventId}:${ticketClassId}:${tag}`, JSON.stringify(design));
  } catch (e) {
    console.warn("localStorage setItem failed (quota exceeded):", e);
  }
  return {
    design,
    preGeneratePrintPdf: Boolean(Number(data?.pre_generate_print_pdf || 0)),
    hasCertificate: data?.hasCertificate !== undefined ? Boolean(data?.hasCertificate) : true,
  };
}

export async function getDefaultTicketDesign(tag = 'withoutProfilePic') {
  const cacheKey = `qe_default_ticket_design_v9_${tag}`;
  const isUsableCachedDefault = (design) => {
    if (!design) return false;
    if (tag === 'badge') {
      const canvas = design.canvas || {};
      const company = design.sections?.companyName;
      const qr = design.sections?.qr;
      const isUpdatedLayout = company && Number(company.y) >= 46 && qr && Number(qr.y) >= 90 && Number(qr.x) === 83;
      return canvas.width === 242 && canvas.height === 242 && isUpdatedLayout;
    }
    if (tag !== 'forPrint' && tag !== 'forCertificatePrint') return true;
    const canvas = design.canvas || {};
    return (
      Number(canvas.borderWidth) === 0 &&
      Number(canvas.borderRadius || 0) === 0 &&
      Number(canvas.borderTopLeftRadius || 0) === 0 &&
      Number(canvas.borderTopRightRadius || 0) === 0 &&
      Number(canvas.borderBottomRightRadius || 0) === 0 &&
      Number(canvas.borderBottomLeftRadius || 0) === 0
    );
  };
  const cachedData = localStorage.getItem(cacheKey);
  if (cachedData) {
    try {
      const cachedDesign = JSON.parse(cachedData);
      if (isUsableCachedDefault(cachedDesign)) {
        return cachedDesign;
      }
      localStorage.removeItem(cacheKey);
    } catch {
      // Ignore cache if malformed
    }
  }

  const query = new URLSearchParams({ tag }).toString();
  const response = await fetch(
    `${environment.apiUrl}/dynamic-ticket/ticketDesign/default?${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  const result = await response.json();
  const design = result?.data?.design || result?.design || null;
  
  if (design) {
    try {
      localStorage.setItem(cacheKey, JSON.stringify(design));
    } catch (e) {
      console.warn("localStorage setItem failed (quota exceeded):", e);
    }
  }
  
  return design;
}

export async function saveTicketDesign(eventId, ticketClassId, design, type = 'ticket_class', tag = 'standard', options = {}) {
  if (!eventId || !ticketClassId) {
    throw new Error("saveTicketDesign: missing eventId or ticketClassId");
  }

  const response = await fetch(
    `${environment.apiUrl}/dynamic-ticket/ticketDesign/save`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        event_id: eventId,
        ticket_class_id: ticketClassId,
        design,
        type,
        tag,
        regenerate_existing: Boolean(options.regenerateExisting),
        pre_generate_print_pdf: Boolean(options.preGeneratePrintPdf)
      }),
    }
  );

  const result = await response.json();
  return result;
}

// used by hook
export async function loadDesign(eventId, ticketClassId, type = 'ticket_class', tag = 'standard') {
  // Always try API first - this is the source of truth
  try {
    const design = await getTicketDesign(eventId, ticketClassId, type, tag);
    if (design) {
      // Cache in localStorage after successful API load
      const key = `${eventId}:${ticketClassId}:${tag}`;
      try {
        localStorage.setItem(LS_PREFIX + key, JSON.stringify(design));
      } catch (e) {
        console.warn("localStorage setItem failed (quota exceeded):", e);
      }
      return design;
    }
  } catch (e) {
    console.error("Failed to load ticket design from API:", e);
  }

  // Only use localStorage as fallback if API fails
  const key = `${eventId}:${ticketClassId}:${tag}`;
  const raw = localStorage.getItem(LS_PREFIX + key);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export async function saveDesign(eventId, ticketClassId, design, type = 'ticket_class', tag = 'standard', options = {}) {
  if (!eventId || !ticketClassId) {
    throw new Error("saveDesign: missing eventId or ticketClassId");
  }

  try {
    // Save to API first (source of truth)
    const result = await saveTicketDesign(eventId, ticketClassId, design, type, tag, options);
    
    // Only cache in localStorage after successful API save
    const key = `${eventId}:${ticketClassId}:${tag}`;
    try {
      localStorage.setItem(LS_PREFIX + key, JSON.stringify(design));
    } catch (e) {
      console.warn("localStorage setItem failed (quota exceeded):", e);
    }
    
    return result;
  } catch (e) {
    console.error("Failed to save ticket design to API:", e);
    throw e;
  }
}

export async function getTicketRegenerationStatus(eventId, options = {}) {
  if (!eventId) {
    return { isActive: false, job: null };
  }

  const params = new URLSearchParams();
  if (options.ticketId) params.set("ticketId", options.ticketId);
  if (options.category) params.set("category", options.category);
  const query = params.toString();
  const response = await fetch(
    `${environment.apiUrl}/ticket/regeneration/status/${eventId}${query ? `?${query}` : ""}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  const result = await response.json();
  return result?.data || result || { isActive: false, job: null };
}

export async function getEventTicketClassesWithDesign(eventId, excludeTicketClassId = null, tag = 'withoutProfilePic') {
  const params = new URLSearchParams({ tag });
  if (excludeTicketClassId) params.set("exclude", excludeTicketClassId);

  const response = await fetch(
    `${environment.apiUrl}/dynamic-ticket/ticketDesign/event/${eventId}/classes?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
    }
  );
  const result = await response.json();
  return result?.data?.classes || [];
}

export async function getTicketDesignForCopy(eventId, sourceTicketClassId, tag = 'withoutProfilePic') {
  return getTicketDesign(eventId, sourceTicketClassId, 'ticket_class', tag);
}

export async function getTicketClassFormFields(eventId, ticketClassId) {
  if (!eventId || !ticketClassId) return [];

  const params = new URLSearchParams({
    event_id: eventId,
    ticket_id: ticketClassId,
  });

  const response = await fetch(
    `${environment.apiUrl}/ticket/getDynamicFieldsByTicketId?${params.toString()}`,
    {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
      },
    },
  );

  const result = await response.json();
  return result?.data?.data || result?.data || result || [];
}
